
import { defineComponent } from 'vue';
import { ITicketMessage, ITicket, IUser, IExtension, IHost, ITheme, ICloud } from '@/types/models';
import api, { ISubmitRequest, IUploadResult } from '@/api/admin/ticketMessages';
import { PER_PAGE } from '@/const';
import { MessageType, TicketStatus } from '@/types/enums';
import utils from '@/utils';
import { auth } from '@/store';
import MessageList from '@/components/tickets/MessageList.vue';

interface IForm {
  content: string;
}

interface IState {
  ticket: ITicket;
  user: IUser;
  count: number;
  messages: ITicketMessage[];
  currentPage: number;
  offset: number;
  limit: number;
  uploadToken: string;
  uploadUrl: string;
  form: IForm;
  isResources: boolean;
  hosts: IHost[];
  clouds: ICloud[];
  themes: ITheme[];
  extensions: IExtension[];
}

export default defineComponent({
  name: 'TicketMessages',
  components: { MessageList },
  data() {
    return {
      ticket: {} as ITicket,
      user: {} as IUser,
      count: 0,
      messages: [],
      currentPage: 1,
      offset: 0,
      limit: PER_PAGE,
      uploadToken: auth.token,
      uploadUrl: api.getUploadUrl(),
      form: {
        content: '',
      },
      isResources: false,
      hosts: [],
      clouds: [],
      themes: [],
      extensions: [],
    } as IState;
  },
  methods: {
    apiGet() {
      const ticketNo = this.$route?.params?.ticketNo as string;
      if (!ticketNo) return;

      api.get(
        {
          ticketNo,
        },
        (res) => {
          this.ticket = res.ticket;
          this.user = res.user;
          this.count = res.count;
          this.messages = res.messages;
        },
      );
    },

    apiSubmit(messageType: MessageType, storageKey: string, content: string) {
      const req: ISubmitRequest = {
        ticketNo: this.ticket.ticketNo,
        messageType,
        storageKey,
        content,
      };
      api.submit(req, (res) => {
        this.form.content = '';
        this.ticket.status = TicketStatus.Waiting;
        this.messages.push(res.message);
      });
    },

    apiClose() {
      api.close(
        {
          ticketNo: this.ticket.ticketNo,
        },
        (res) => {
          if (!res.value) return;
          utils.success('工单关闭成功！');
          this.ticket.status = TicketStatus.Closed;
        },
      );
    },

    apiOpen() {
      api.open(
        {
          ticketNo: this.ticket.ticketNo,
        },
        (res) => {
          if (!res.value) return;
          utils.success('工单重新打开成功！');
          this.ticket.status = TicketStatus.Dealing;
        },
      );
    },

    apiResources() {
      api.resources(this.user.id, (res) => {
        if (!res) return;

        this.isResources = true;
        this.hosts = res.hosts;
        this.clouds = res.cloud != null ? [res.cloud] : [];
        this.themes = res.themes;
        this.extensions = res.extensions;
      });
    },

    btnResourcesClick() {
      this.apiResources();
    },

    getHostName(host: IHost) {
      return utils.getHostUniqueName(host);
    },

    getHostType(host: IHost) {
      return utils.getHostType(host.hostType);
    },

    getCloudType(cloud: ICloud) {
      return utils.getCloudType(cloud.cloudType);
    },

    getUrl(hostName: string, nodeName: string, isHongKong: boolean): string {
      return utils.getHostUrl(hostName, nodeName, isHongKong);
    },

    getTicketPriority() {
      return utils.getTicketPriority(this.ticket.priority);
    },

    getTicketStatusType(ticket: ITicket) {
      if (ticket.status === TicketStatus.Closed) {
        return 'success';
      }
      if (ticket.status === TicketStatus.Dealing) {
        return 'danger';
      }
      return 'primary';
    },

    getTicketCategory() {
      return utils.getTicketCategory(this.ticket.category);
    },

    getTicketStatus(status: TicketStatus) {
      if (status === TicketStatus.Waiting) return '待用户回复';
      if (status === TicketStatus.Dealing) return '待处理';
      if (status === TicketStatus.Closed) return '工单已关闭';
      return '全部';
    },

    isInputArea() {
      return this.ticket.status !== TicketStatus.Closed;
    },

    btnSubmitClick() {
      if (this.form.content) {
        this.apiSubmit(MessageType.Text, '', this.form.content);
      }
    },

    btnOpenClick() {
      this.apiOpen();
    },

    btnCloseClick() {
      utils.alertDelete(
        {
          title: '关闭工单',
          text: '此操作将关闭工单，确定吗？',
          button: '确认关闭',
        },
        () => {
          this.apiClose();
        },
      );
    },

    uploadBefore(file: { size: number }) {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        utils.error('文件大小不能超过 10MB!');
      }
      return isLt10M;
    },

    uploadProgress() {
      utils.loading(true);
    },

    uploadSuccess(res: IUploadResult) {
      this.apiSubmit(res.messageType, res.storageKey, res.content);
    },

    uploadError(err: { message: string }) {
      utils.loading(false);
      const error = JSON.parse(err.message);
      utils.error(error.message);
    },
  },
  created() {
    this.$watch(
      () => this.$route.params,
      () => {
        this.apiGet();
      },
      // fetch the data when the view is created and the data is
      // already being observed
      { immediate: true },
    );
  },
});
